import { Flex, Dropdown, Button, Input, Avatar, Divider, Typography, message, Modal } from 'antd';
import {
  CaretDown,
  List,
  User,
  Wallet,
  SignOut,
  Crown,
  ClockCounterClockwise,
  Copy,
  ArrowsClockwise,
  Book,
  Download,
} from '@phosphor-icons/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { type RouteCfgType, RoutesCfg } from '~/routes/const';
import { useResponsive } from '~/hooks/useResponsive';
import { useAuth } from '~/context/AuthContext ';
import { getLoginModalStore } from '../LoginModal/store';
import { formatAmount, generateUUID, getVipExpireShow, getVipInfo, isPhone } from '~/utils/function';
import { THEME_COLOR } from '~/utils/const';
import { PackagesTabKey } from '~/pages/Packages';

import { DocsHerf } from '~/pages/UserInfo/MoreTool';
import { updateApiToken } from '~/services/user';
import { formatToDateTimeMin } from '~/utils/date';

import './index.less';

const defaultHomeActive = (path: string, currentPath: string) =>
  path === currentPath || (path === '/home' && currentPath === '/');

// 手机端底部导航显示的菜单项路径
const mobileNavPaths = ['/home', '/workflow', '/jcaigc-doc', '/user-info'];

// 手机端底部导航组件
export const MobileBottomNav = () => {
  const location = useLocation();

  const navItems = useMemo(() => {
    return RoutesCfg.filter((item) => mobileNavPaths.includes(item.path));
  }, []);

  return (
    <div className="mobile-bottom-nav">
      {navItems.map((item) => {
        const isActive = defaultHomeActive(item.path, location.pathname);

        // 技术文档 - 有下拉菜单
        if (item.links) {
          return (
            <div
              key={item.path}
              className={`mobile-nav-item ${isActive ? 'active' : ''}`}
              onClick={() => {
                const firstLink = item.links?.[0];
                if (firstLink) {
                  window.open(firstLink.href, '_blank');
                }
              }}
            >
              <item.icon weight={isActive ? 'fill' : 'regular'} size={22} />
              <span>{item.text}</span>
            </div>
          );
        }

        // 外部链接
        if (item.href) {
          return (
            <a
              key={item.path}
              className="mobile-nav-item"
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              <item.icon weight="regular" size={22} />
              <span>{item.text}</span>
            </a>
          );
        }

        // 内部路由
        return (
          <Link
            key={item.path}
            to={item.path}
            className={`mobile-nav-item ${isActive ? 'active' : ''}`}
          >
            <item.icon weight={isActive ? 'fill' : 'regular'} size={22} />
            <span>{item.text}</span>
          </Link>
        );
      })}
    </div>
  );
};

const NavModule = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isMobile } = useResponsive();

  const { userInfo = {}, logout, fetchUserInfo } = useAuth();

  const [showMenu, setShowMenu] = useState(false);
  const [navItems, setNavItems] = useState<RouteCfgType[]>(
    RoutesCfg.map((item) => ({
      ...item,
      active: defaultHomeActive(item.path, location.pathname),
    }))
  );
  const [rotatedItemKey, setRotatedItemKey] = useState<string | null>(null);

  const startUpdateApiToken = async () => {
    const id = userInfo?.id as string;
    const { code, message: msg } = await updateApiToken({ id, apiKey: generateUUID() });
    if (code === 0) {
      await fetchUserInfo(id);
    } else {
      message.error(msg || '操作失败');
    }
  };

  const handleUpdateToken = () => {
    Modal.confirm({
      title: '更新API秘钥',
      content: (
        <>
          更新秘钥之后，所用到本秘钥的工作流（如云渲染节点）将全部失效
          <br />
          <br />
          您确定要更新API秘钥吗？
        </>
      ),
      onOk() {
        startUpdateApiToken();
      },
    });
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success('已复制到剪贴板');
    } catch (err) {
      message.error('复制失败，请手动复制');
    }
  };

  const vipInfo = useMemo(() => {
    return getVipInfo(userInfo?.vipLevel as number);
  }, [userInfo?.vipLevel]);

  useEffect(() => {
    if (location.pathname !== null) {
      setNavItems(
        navItems.map((item) => ({
          ...item,
          active: defaultHomeActive(item.path, location.pathname),
        }))
      );
    }
  }, [location.pathname]);

  const _NavItem = (items: RouteCfgType[]) => {
    return (
      <>
        {items.map((item, index) => {
          if (item.hideInMenu) return null;
          const _IconText = () => (
            <>
              <item.icon weight="fill" size={20} {...item.iconProps} />
              {item.text}
              {item.links && (
                <CaretDown
                  size={14}
                  weight="bold"
                  className={`ml-1 ${rotatedItemKey === item.path ? 'rotate-icon' : ''}`}
                />
              )}
            </>
          );

          // 有下拉菜单的情况
          if (item.links) {
            const menuProps = {
              className: 'nav-dropdown-menu',
              items: item.links.map((link, linkIndex) => ({
                key: linkIndex,
                label: (
                  <a
                    href={link.href}
                    target="_blank"
                    className="flex items-center gap-2"
                    onClick={() => setShowMenu(!showMenu)}
                  >
                    <link.icon weight="fill" size={20} />
                    {link.text}
                  </a>
                ),
              })),
              onMouseEnter: () => setRotatedItemKey(item.path),
              onMouseLeave: () => setRotatedItemKey(null),
            };

            return (
              <Dropdown key={index} menu={menuProps} placement="bottom">
                <a
                  className={`nav-item ${rotatedItemKey === item.path ? 'hover' : ''}`}
                  onClick={(e) => e.preventDefault()}
                  onMouseEnter={() => setRotatedItemKey(item.path)}
                  onMouseLeave={() => setRotatedItemKey(null)}
                >
                  <_IconText />
                </a>
              </Dropdown>
            );
          }

          // 外部链接
          if (item.href) {
            return (
              <a className="nav-item" href={item.href} target="_blank" key={index} onClick={() => setShowMenu(!showMenu)}>
                <_IconText />
              </a>
            );
          }

          // 内部路由
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setShowMenu(!showMenu)}
              className={`nav-item ${item.active ? 'active' : ''}`}
            >
              <_IconText />
            </Link>
          );
        })}
      </>
    );
  };

  return (
    <>
      <Flex className={`flex-1 zt-nav-ct`} justify="flex-end" align="center">
        {isMobile ? (
          <>
            <div className="custom-toggler mr-2" onClick={() => setShowMenu(!showMenu)}>
              <List size={32} />
            </div>
            {showMenu && (
              <Flex className="zt-nav zt-nav_expand" vertical>
                {_NavItem(navItems)}
              </Flex>
            )}
          </>
        ) : (
          <Flex className="zt-nav">{_NavItem(navItems)}</Flex>
        )}
      {userInfo?.id ? (
        <Dropdown
          menu={{
            className: 'user-dropdown-menu',
            items: [
              {
                key: 'user-info',
                label: (
                  <>
                    <div className="user-dropdown-info">
                      <div className="user-dropdown-avatar">
                        <Avatar size={48}>
                          <User weight="fill" size={24} />
                        </Avatar>
                        <div className={`user-level-badge ${vipInfo.class}`}>
                          {userInfo?.vipLevel === 0 ? '普通' : vipInfo.text}
                        </div>
                      </div>
                      <div className="user-dropdown-details">
                        <div className="user-dropdown-name">{userInfo.name || '未登录'}</div>
                        <div className="user-dropdown-id">
                          ID: {userInfo.id || '-'}
                          <Typography.Text
                            copyable={{ text: userInfo.id || '-' }}
                            className="ml-1 packages-user-id-copy"
                          />
                        </div>
                        <div className="user-dropdown-id">
                          会员有效期：{getVipExpireShow(userInfo)}
                        </div>
                        <div className="user-dropdown-balance">
                          <Wallet weight="fill" size={16} color={THEME_COLOR} />
                          积分余额：
                          <span className="user-dropdown-balance-text">
                            {formatAmount(userInfo.points || 0)}
                          </span>
                          <Button
                            type="link"
                            className="user-dropdown-recharge"
                            onClick={() =>
                              navigate(`/recharge?type=${PackagesTabKey.PLUGIN}`, { replace: true })
                            }
                          >
                            充值
                          </Button>
                        </div>
                      </div>
                    </div>
                    <Flex justify="center" className="px-[12px]">
                      <Button
                        type="primary"
                        className="flex-1"
                        icon={<Crown size={18} weight="fill" />}
                        onClick={() => navigate(`/recharge?type=${PackagesTabKey.VIP}`)}
                      >
                        成为会员
                      </Button>
                    </Flex>
                    <Divider className="my-3" />
                    <div className="user-dropdown-token px-[12px]">
                      <div className="user-dropdown-token_label">插件秘钥 (apiKey)</div>
                      <Input.Password
                        value={userInfo.apiKey || ''}
                        className="user-dropdown-token_input my-3"
                        readOnly
                        onClick={() => copyToClipboard(userInfo.apiKey || '')}
                      />
                      <Flex className="user-dropdown-token_btns" gap={8}>
                        <Button
                          color="primary"
                          variant="filled"
                          className="flex-1"
                          icon={<Copy size={18} weight="fill" />}
                          onClick={() => copyToClipboard(userInfo.apiKey || '')}
                        >
                          复制
                        </Button>
                        <Button
                          color="primary"
                          variant="filled"
                          className="flex-1"
                          icon={<ArrowsClockwise size={18} weight="fill" />}
                          onClick={handleUpdateToken}
                        >
                          更新
                        </Button>
                      </Flex>
                    </div>
                    <Divider className="my-3" />
                    <div className="user-dropdown-tool">
                      <div
                        className="user-dropdown-tool-item"
                        onClick={() => navigate('/user-info')}
                      >
                        <User size={18} weight="fill" color={THEME_COLOR} />
                        <span>个人中心</span>
                      </div>
                      <div
                        className="user-dropdown-tool-item"
                        onClick={() => navigate(`/recharge?type=${PackagesTabKey.VIP}`)}
                      >
                        <Crown size={18} weight="fill" color={THEME_COLOR} />
                        <span>会员购买</span>
                      </div>
                      <div
                        className="user-dropdown-tool-item"
                        onClick={() => navigate(`/recharge?type=${PackagesTabKey.PLUGIN}`)}
                      >
                        <Wallet size={18} weight="fill" color={THEME_COLOR} />
                        <span>插件充值</span>
                      </div>
                      <div
                        className="user-dropdown-tool-item"
                        onClick={() => navigate(`/recharge?type=${PackagesTabKey.CONSUMPTION}`)}
                      >
                        <ClockCounterClockwise size={18} weight="bold" color={THEME_COLOR} />
                        <span>消费明细</span>
                      </div>
                      <div
                        className="user-dropdown-tool-item"
                        onClick={() => window.open(DocsHerf, '_blank')}
                      >
                        <Book size={18} weight="fill" color={THEME_COLOR} />
                        <span>官方文档</span>
                      </div>
                      <div
                        className="user-dropdown-tool-item"
                        onClick={() => navigate(`/download`)}
                      >
                        <Download size={18} weight="fill" color={THEME_COLOR} />
                        <span>剪映助手</span>
                      </div>
                    </div>
                    <Flex className="user-dropdown-logout px-[12px] pt-1 pb-2">
                      <Button
                        color="default"
                        variant="filled"
                        className="user-dropdown-logout-btn flex-1"
                        onClick={() => logout()}
                      >
                        <SignOut size={18} weight="bold" />
                        <span>退出登录</span>
                      </Button>
                    </Flex>
                  </>
                ),
                disabled: true,
              },
            ],
          }}
          // trigger={['click']}
        >
          <Flex className="nav-user-avatar" align="center" onClick={() => navigate('/user-info')}>
            {userInfo.name?.slice(0, 1)}
          </Flex>
        </Dropdown>
        ) : (
          <Button
            type="primary"
            className="login-btn"
            onClick={() => (getLoginModalStore().open = true)}
          >
            登录
          </Button>
        )}
      </Flex>
    </>
  );
};

export default NavModule;
